import {inject, Injectable} from '@angular/core';
import {AuthApiService} from '../api/auth-api.service';
import {TokenStorageService} from './token-storage.service';
import {SessionStore} from '../stores/session.store';
import {firstValueFrom, Subject} from 'rxjs';
import {jwtDecode} from 'jwt-decode';
import {DecodedJwt} from '../utils/jwt.utils';

@Injectable({ providedIn: 'root' })
export class TokenRefreshService {

	private api = inject(AuthApiService);
	private storage = inject(TokenStorageService);
	private session = inject(SessionStore);

	private refreshFailed = new Subject<void>();
	refreshFailed$ = this.refreshFailed.asObservable();

	private refreshPromise: Promise<string | null> | null = null;
	private refreshTimer?: number | null;

	scheduleFromToken(jwt: string) {

		const decoded = jwtDecode<DecodedJwt>(jwt);

		const expiresAt = decoded.exp * 1000;
		const refreshAt = expiresAt - 3 * 60 * 1000;

		const delay = refreshAt - Date.now();

		if (delay <= 0) {
			this.refreshToken();
			return;
		}

		if (this.refreshTimer) {
			clearTimeout(this.refreshTimer);
		}

		this.refreshTimer = setTimeout(() => {
			this.refreshToken();
		}, delay);
	}

	async refreshToken(): Promise<string | null> {

		if (this.refreshPromise) {
			return this.refreshPromise;
		}

		const tokens = this.storage.get();
		if (!tokens?.updateToken) return null;

		this.refreshPromise = this.executeRefresh(tokens.updateToken);

		const result = await this.refreshPromise;

		this.refreshPromise = null;

		if (!result) {
			this.refreshFailed.next();
		}

		return result;
	}


	private async executeRefresh(updateToken: string): Promise<string | null> {

		try {
			const newTokens = await firstValueFrom(
				this.api.refresh(updateToken)
			);

			this.storage.save(newTokens);
			this.session.startSession(newTokens.jwt);
			this.scheduleFromToken(newTokens.jwt);

			return newTokens.jwt;

		} catch {
			this.refreshFailed.next();
			return null;
		}
	}


	clearTimer() {
		if (this.refreshTimer) {
			clearTimeout(this.refreshTimer);
			this.refreshTimer = null;
		}
	}
}
